import { z } from 'zod';
/**
 * Platform settings.
 *
 * Each group is one JSON document in `PlatformSetting`, validated by the schema
 * below. The database holds JSON; this file holds the contract, so the admin
 * panel and the API cannot disagree about the shape of a group.
 *
 * Every group has a `DEFAULT_*` beside it. A settings page must be able to
 * render before anyone has ever saved it, and a page that crashes on a missing
 * key is a page nobody can use to set the key.
 */
export const SETTING_KEYS = [
    'general',
    'seo',
    'social_login',
    'maintenance',
    'mail',
    'footer',
    'security',
    'analytics',
    'backup',
    'integrations',
    'catalogues',
    'plan_features',
    'billing',
    'appearance',
];
// ── General ─────────────────────────────────────────────────────────────────
/**
 * Date formats offered platform-wide.
 *
 * `DD/MM/YYYY` is the default because this is an Indian product and `03/04`
 * means 3 April to every user of it. The ISO option exists for exports and for
 * anyone who has been bitten by the ambiguity.
 */
export const DATE_FORMATS = [
    { value: 'DD/MM/YYYY', label: '31/12/2026', hint: 'Day first — the Indian convention' },
    { value: 'DD/MM/YY', label: '31/12/26', hint: 'Day first, short year' },
    { value: 'DD MMM YYYY', label: '31 Dec 2026', hint: 'Unambiguous, good for notices' },
    { value: 'MM/DD/YYYY', label: '12/31/2026', hint: 'Month first — US convention' },
    { value: 'YYYY-MM-DD', label: '2026-12-31', hint: 'ISO 8601 — sorts correctly' },
];
export const TIME_FORMATS = [
    { value: 'h:mm a', label: '9:41 pm' },
    { value: 'HH:mm', label: '21:41' },
];
/** Every language the web surfaces can be translated into; `enabledLocales` picks from these. */
export const UI_LOCALES = ['en', 'hi', 'mr', 'gu', 'ta', 'te', 'kn'];
export const UI_LOCALE_NAMES = {
    en: { name: 'English', native: 'English' },
    hi: { name: 'Hindi', native: 'हिन्दी' },
    mr: { name: 'Marathi', native: 'मराठी' },
    gu: { name: 'Gujarati', native: 'ગુજરાતી' },
    ta: { name: 'Tamil', native: 'தமிழ்' },
    te: { name: 'Telugu', native: 'తెలుగు' },
    kn: { name: 'Kannada', native: 'ಕನ್ನಡ' },
};
export const GeneralSettingsSchema = z.object({
    siteName: z.string().min(1).max(80).default('KinshipOS'),
    /**
     * The console's own branding, so renaming the product does not need a build.
     * `logoUrl` empty falls back to the first letter of `siteName`, which is what
     * the sidebar has always drawn; `faviconUrl` empty keeps the bundled icon.
     */
    logoUrl: z.string().max(500).default(''),
    faviconUrl: z.string().max(500).default(''),
    /**
     * What the sidebar shows at the top of both consoles. 'logo' suits a
     * wordmark that already contains the name; 'name' is the sensible choice
     * when no logo has been uploaded.
     */
    /**
     * Optional dark-theme variants. A mark drawn for a light background usually
     * disappears on a dark one; leaving these empty keeps the single logo and
     * icon above in both themes, which is right for anything already legible on
     * either.
     */
    logoUrlDark: z.string().max(500).default(''),
    faviconUrlDark: z.string().max(500).default(''),
    consoleBrandDisplay: z.enum(['both', 'logo', 'name']).default('both'),
    /**
     * Height of the sidebar logo in pixels. The header is 56px tall, so 48 is
     * the practical ceiling; a wordmark usually needs less height than a square
     * mark, not more.
     */
    consoleLogoHeight: z.number().int().min(16).max(48).default(32),
    supportEmail: z.string().email().default('support@kinshipos.com'),
    supportPhone: z.string().max(20).default(''),
    /** Where the admin panel's help button goes. Empty means the site's own contact page. */
    helpUrl: z.string().url().max(300).or(z.literal('')).default(''),
    /**
     * Whether a website sign-up starts on a free trial at all. Off, a new
     * society pays for its plan before it can do anything, and the site's
     * buttons say "Get started" rather than "Start free trial".
     */
    trialEnabled: z.boolean().default(true),
    /** The plan a website sign-up trials on — its own "trial days" sets the length. */
    trialPlanCode: z.string().min(1).max(40).default('growth'),
    /** The company behind the platform, as printed on its GST invoices to societies. */
    legalName: z.string().max(160).default(''),
    gstin: z.string().max(15).default(''),
    legalAddress: z.string().max(300).default(''),
    dateFormat: z
        .enum(['DD/MM/YYYY', 'DD/MM/YY', 'DD MMM YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD'])
        .default('DD/MM/YYYY'),
    timeFormat: z.enum(['h:mm a', 'HH:mm']).default('h:mm a'),
    /** ISO 3166-1 alpha-2. Narrows the timezone list offered in the console. */
    country: z.string().length(2).default('IN'),
    timezone: z.string().max(64).default('Asia/Kolkata'),
    // No `currencySymbol` here on purpose. A symbol typed by hand beside a
    // currency code can only drift from it — set to USD with '₹' still in the
    // box and every screen lies. The symbol comes from the currency code in
    // Settings → Payments, which is the one place currency is chosen.
    defaultLocale: z.string().max(10).default('en'),
    /**
     * The languages the admin panel offers, in the order the picker lists
     * them. English is always present. The website is English-only; the
     * resident app has its own list (its ARB files).
     */
    enabledLocales: z.array(z.enum(UI_LOCALES)).min(1).default(['en', 'hi', 'mr', 'gu', 'ta', 'te']),
});
export const CONSOLE_BRAND_DISPLAYS = [
    { value: 'both', label: 'Logo and name', hint: 'The mark beside the product name. The default.' },
    { value: 'logo', label: 'Logo only', hint: 'For a wordmark that already spells the name out.' },
    { value: 'name', label: 'Name only', hint: 'No mark at all — just the name in text.' },
];
export const DEFAULT_GENERAL = GeneralSettingsSchema.parse({});
// ── SEO ─────────────────────────────────────────────────────────────────────
export const SeoSettingsSchema = z.object({
    metaTitle: z.string().max(70).default('KinshipOS — society management software for Indian communities'),
    // 160 is where Google truncates. Longer is not wrong, just unread.
    metaDescription: z.string().max(160).default(''),
    metaKeywords: z.array(z.string().max(40)).max(20).default([]),
    ogImageUrl: z.string().max(500).default('/og.png'),
    twitterHandle: z.string().max(40).default(''),
    canonicalHost: z.string().max(120).default('https://kinshipos.com'),
    googleSiteVerification: z.string().max(120).default(''),
    bingSiteVerification: z.string().max(120).default(''),
    robotsAllowIndexing: z.boolean().default(true),
    /// Appended verbatim to the generated robots.txt.
    robotsExtra: z.string().max(4000).default(''),
    sitemapEnabled: z.boolean().default(true),
    sitemapChangeFreq: z
        .enum(['always', 'hourly', 'daily', 'weekly', 'monthly', 'yearly', 'never'])
        .default('weekly'),
    /**
     * `llms.txt` — the emerging convention for telling an LLM crawler what a site
     * is and which pages are worth reading. Not a standard yet, and costs one
     * static file to support.
     */
    llmsTxtEnabled: z.boolean().default(true),
    llmsTxtSummary: z.string().max(2000).default(''),
    /// Whether AI crawlers may train on the site. Advisory, and worth stating.
    llmsAllowTraining: z.boolean().default(false),
});
export const DEFAULT_SEO = SeoSettingsSchema.parse({});
// ── Social login ────────────────────────────────────────────────────────────
export const SOCIAL_PROVIDERS = [
    { id: 'google', label: 'Google', note: 'The one Indian users actually have.' },
    { id: 'facebook', label: 'Facebook' },
    { id: 'apple', label: 'Apple', note: 'Required by App Store review if any other social login is offered.' },
    { id: 'linkedin', label: 'LinkedIn' },
    { id: 'x', label: 'X' },
    { id: 'github', label: 'GitHub' },
    { id: 'amazon', label: 'Amazon' },
];
const SocialProviderConfigSchema = z.object({
    enabled: z.boolean().default(false),
    clientId: z.string().max(200).default(''),
    /**
     * Write-only from the panel's point of view: the API returns a masked value
     * and only overwrites the stored secret when a new one is sent. A settings
     * page that echoes secrets back is a settings page that leaks them into
     * every browser cache and screen share.
     */
    clientSecret: z.string().max(400).default(''),
});
/**
 * Apple does not have a client secret in the ordinary sense.
 *
 * It wants a short-lived ES256 JWT signed with a .p8 key, which means three
 * extra pieces of configuration no other provider needs. Giving Apple its own
 * shape is more honest than overloading `clientSecret` with a PEM and hoping
 * whoever fills the form guesses.
 */
const AppleProviderConfigSchema = SocialProviderConfigSchema.extend({
    /// From the Apple Developer membership page.
    teamId: z.string().max(20).default(''),
    /// The Key ID of the Sign in with Apple key.
    keyId: z.string().max(20).default(''),
    /// The .p8 private key, PEM-encoded. Write-only, like every other secret.
    privateKey: z.string().max(4000).default(''),
});
export const SocialLoginSettingsSchema = z.object({
    google: SocialProviderConfigSchema.default({}),
    facebook: SocialProviderConfigSchema.default({}),
    apple: AppleProviderConfigSchema.default({}),
    linkedin: SocialProviderConfigSchema.default({}),
    x: SocialProviderConfigSchema.default({}),
    github: SocialProviderConfigSchema.default({}),
    amazon: SocialProviderConfigSchema.default({}),
});
export const DEFAULT_SOCIAL_LOGIN = SocialLoginSettingsSchema.parse({});
// ── Maintenance mode ────────────────────────────────────────────────────────
export const MaintenanceSettingsSchema = z.object({
    enabled: z.boolean().default(false),
    title: z.string().max(120).default('We are doing some maintenance'),
    message: z
        .string()
        .max(1000)
        .default('KinshipOS is briefly unavailable while we upgrade. Nothing you have saved is affected.'),
    /// ISO timestamp. Drives the countdown the visitor sees.
    endsAt: z.string().datetime().nullable().default(null),
    /**
     * IPs that get through anyway — the people doing the maintenance need to see
     * whether it worked. Without this, maintenance mode locks out the person who
     * turned it on.
     */
    allowedIps: z.array(z.string().max(45)).max(20).default([]),
    /// The admin panel usually stays up while the marketing site is down.
    affectsAdminPanel: z.boolean().default(false),
    affectsApi: z.boolean().default(false),
});
export const DEFAULT_MAINTENANCE = MaintenanceSettingsSchema.parse({});
// ── Mail / SMTP ─────────────────────────────────────────────────────────────
export const MailSettingsSchema = z.object({
    driver: z.enum(['smtp', 'ses', 'console']).default('console'),
    fromName: z.string().max(80).default('KinshipOS'),
    fromEmail: z.string().email().default('no-reply@kinshipos.com'),
    replyTo: z.string().max(120).default(''),
    smtpHost: z.string().max(200).default(''),
    smtpPort: z.coerce.number().int().min(1).max(65_535).default(587),
    smtpUser: z.string().max(200).default(''),
    smtpPassword: z.string().max(400).default(''),
    /// STARTTLS on 587, implicit TLS on 465.
    smtpSecure: z.boolean().default(false),
    sesRegion: z.string().max(40).default('ap-south-1'),
});
export const DEFAULT_MAIL = MailSettingsSchema.parse({});
// ── Billing: currencies, plan-payment gateways, bank transfer ──────────────
/** ISO 4217 code, upper case. */
const currencyCode = z.string().regex(/^[A-Z]{3}$/, 'Three-letter currency code');
export const CHECKOUT_GATEWAYS = ['stripe', 'paypal', 'razorpay', 'paystack', 'mercadopago'];
/**
 * How societies pay the platform for their plan. Prices are kept in the
 * base currency; a society sees and pays in its own country's currency at
 * the rate held here. Each gateway is switched on by the platform owner and
 * offered only to societies whose currency it supports.
 */
export const BillingSettingsSchema = z.object({
    baseCurrency: currencyCode.default('INR'),
    /** `rate` = how many units of this currency one unit of the base currency buys. */
    currencies: z
        .array(z.object({ code: currencyCode, rate: z.coerce.number().positive(), enabled: z.boolean().default(true) }))
        .max(60)
        .default([{ code: 'INR', rate: 1, enabled: true }]),
    /** Refresh the rates daily from the public rate service; a rate edited by hand is kept. */
    autoRates: z.boolean().default(true),
    ratesUpdatedAt: z.string().default(''),
    /** On the platform's invoices: prefix and tax line. */
    invoicePrefix: z.string().min(1).max(12).default('INV'),
    taxLabel: z.string().max(20).default('GST'),
    taxRatePercent: z.coerce.number().min(0).max(60).default(18),
    gateways: z
        .object({
        stripe: z
            .object({
            enabled: z.boolean().default(false),
            publishableKey: z.string().max(200).default(''),
            secretKey: z.string().max(200).default(''),
            webhookSecret: z.string().max(200).default(''),
        })
            .default({}),
        paypal: z
            .object({
            enabled: z.boolean().default(false),
            clientId: z.string().max(200).default(''),
            clientSecret: z.string().max(200).default(''),
            sandbox: z.boolean().default(true),
        })
            .default({}),
        razorpay: z
            .object({
            enabled: z.boolean().default(false),
            keyId: z.string().max(120).default(''),
            keySecret: z.string().max(200).default(''),
        })
            .default({}),
        paystack: z
            .object({
            enabled: z.boolean().default(false),
            publicKey: z.string().max(200).default(''),
            secretKey: z.string().max(200).default(''),
        })
            .default({}),
        mercadopago: z
            .object({
            enabled: z.boolean().default(false),
            publicKey: z.string().max(200).default(''),
            accessToken: z.string().max(300).default(''),
        })
            .default({}),
    })
        .default({}),
    /** Bank transfer / UPI: the society pays outside the system and uploads proof. */
    manual: z
        .object({
        enabled: z.boolean().default(true),
        title: z.string().max(60).default('Bank transfer / UPI'),
        upiId: z.string().max(80).default(''),
        upiQrUrl: z.string().max(500).default(''),
        bankName: z.string().max(80).default(''),
        accountName: z.string().max(120).default(''),
        accountNumber: z.string().max(40).default(''),
        ifsc: z.string().max(20).default(''),
        swift: z.string().max(20).default(''),
        instructions: z.string().max(600).default('Pay the amount shown, then upload the receipt or screenshot. Your plan is activated once we confirm the payment, usually within one working day.'),
    })
        .default({}),
});
/**
 * A society's own collection set-up (Society → Settings → Payments): the same
 * gateways and bank-transfer details the platform uses for plan purchases,
 * but pointing at the society's accounts, because dues are the society's
 * money. `inherit` keeps the platform's gateways until the society sets its
 * own; the resident app draws whichever set is in force.
 */
export const SocietyPaymentSettingsSchema = z.object({
    inherit: z.boolean().default(true),
    /** The currency dues are billed and paid in; '' = the platform's default for the society's country. */
    currency: z.string().trim().toUpperCase().regex(/^[A-Z]{3}$/).or(z.literal('')).default(''),
    gateways: BillingSettingsSchema.shape.gateways,
    manual: BillingSettingsSchema.shape.manual,
});
export const DEFAULT_SOCIETY_PAYMENT_SETTINGS = SocietyPaymentSettingsSchema.parse({});
// ── Footer ──────────────────────────────────────────────────────────────────
export const FooterSettingsSchema = z.object({
    logoUrl: z.string().max(500).default(''),
    tagline: z.string().max(200).default(''),
    copyrightName: z.string().max(120).default('Media City Technologies'),
    social: z
        .object({
        facebook: z.string().max(300).default(''),
        x: z.string().max(300).default(''),
        linkedin: z.string().max(300).default(''),
        instagram: z.string().max(300).default(''),
        whatsapp: z.string().max(300).default(''),
        youtube: z.string().max(300).default(''),
    })
        .default({}),
    apps: z
        .object({
        androidUrl: z.string().max(300).default(''),
        iosUrl: z.string().max(300).default(''),
        /// A direct APK, for societies whose residents cannot reach Play.
        directApkUrl: z.string().max(300).default(''),
    })
        .default({}),
});
export const DEFAULT_FOOTER = FooterSettingsSchema.parse({});
// ── Security ────────────────────────────────────────────────────────────────
export const SecuritySettingsSchema = z.object({
    /// Failed attempts before the account is locked.
    maxLoginAttempts: z.coerce.number().int().min(3).max(20).default(5),
    /// How long the lock lasts. Long enough to defeat a script, short enough
    /// that a real person who fat-fingered their password is not locked out for
    /// the afternoon.
    lockoutMinutes: z.coerce.number().int().min(1).max(1440).default(15),
    requireTotpForPlatform: z.boolean().default(true),
    sessionIdleMinutes: z.coerce.number().int().min(5).max(1440).default(60),
    recaptchaEnabled: z.boolean().default(false),
    recaptchaSiteKey: z.string().max(200).default(''),
    recaptchaSecretKey: z.string().max(400).default(''),
    /// v3 returns a score; below this the request is treated as a bot.
    recaptchaMinScore: z.coerce.number().min(0).max(1).default(0.5),
    /// CIDR or a bare address. Checked before anything else.
    blockedIps: z.array(z.string().max(45)).max(500).default([]),
    /// ISO 3166-1 alpha-2. Resolved from the IP by the edge.
    blockedCountries: z.array(z.string().length(2)).max(250).default([]),
    /**
     * Inverts the country list: only the listed countries may reach the site.
     * A society platform serving one country has a much smaller attack surface
     * with this on, but it also locks out an NRI owner travelling — hence off.
     */
    countryAllowListMode: z.boolean().default(false),
});
export const DEFAULT_SECURITY = SecuritySettingsSchema.parse({});
// ── Analytics ───────────────────────────────────────────────────────────────
export const AnalyticsSettingsSchema = z.object({
    googleAnalyticsId: z.string().max(40).default(''),
    googleTagManagerId: z.string().max(40).default(''),
    facebookPixelId: z.string().max(40).default(''),
    /**
     * Whether trackers load before a visitor opts in. Defaults to false and
     * should stay there: loading a tracker without consent is the most-enforced
     * breach in the EU (see docs/compliance/gdpr-gap-analysis.md §2.11).
     */
    loadWithoutConsent: z.boolean().default(false),
});
export const DEFAULT_ANALYTICS = AnalyticsSettingsSchema.parse({});
// ── Backup ──────────────────────────────────────────────────────────────────
export const BackupSettingsSchema = z.object({
    scheduleEnabled: z.boolean().default(false),
    /// Cron, IST. Default is 02:30 daily — after the nightly bill run.
    cron: z.string().max(60).default('30 2 * * *'),
    retainCopies: z.coerce.number().int().min(1).max(90).default(14),
    destination: z.enum(['local', 'google_drive', 's3']).default('local'),
    googleDriveFolderId: z.string().max(120).default(''),
    /// Service-account JSON. Write-only, like every other secret here.
    googleDriveCredentials: z.string().max(8000).default(''),
    /// Blank uses the bucket the API is already configured with.
    s3Bucket: z.string().max(120).default(''),
    s3Prefix: z.string().max(120).default('backups'),
    notifyEmail: z.string().max(120).default(''),
});
export const DEFAULT_BACKUP = BackupSettingsSchema.parse({});
// ── Integrations ────────────────────────────────────────────────────────────
/**
 * Third-party credentials.
 *
 * Every key here is a *write-only* field: the API encrypts it at rest
 * (AES-256-GCM under `ENCRYPTION_KEY`), returns a mask to the browser, and
 * only overwrites the stored value when a new one is sent. The environment
 * file is consulted only when a value has never been saved here, so a fresh
 * install keeps working and a configured one never depends on `.env`.
 *
 * `ENCRYPTION_KEY` itself, the database and Redis URLs and the JWT secrets
 * stay in the environment: they are what unlocks this table, and a key stored
 * inside the thing it protects protects nothing.
 */
export const IntegrationsSettingsSchema = z.object({
    payments: z
        .object({
        gateway: z.enum(['mock', 'razorpay']).default('mock'),
        razorpayKeyId: z.string().max(120).default(''),
        razorpayKeySecret: z.string().max(200).default(''),
        razorpayWebhookSecret: z.string().max(200).default(''),
    })
        .default({}),
    sms: z
        .object({
        driver: z.enum(['console', 'msg91', 'twilio']).default('console'),
        msg91AuthKey: z.string().max(200).default(''),
        msg91SenderId: z.string().max(20).default(''),
        msg91OtpTemplateId: z.string().max(80).default(''),
        twilioAccountSid: z.string().max(80).default(''),
        twilioAuthToken: z.string().max(200).default(''),
        twilioFrom: z.string().max(30).default(''),
        /** A Content Template SID (HX…) — trial accounts may only send through Twilio's pre-approved templates. */
        twilioContentSid: z.string().max(60).default(''),
        /**
         * A Verify Service SID (VA…). When set, login codes are sent and checked
         * through Twilio Verify instead of plain SMS — the only route a Twilio
         * trial account allows, and Twilio's recommended one for OTP anyway.
         */
        twilioVerifySid: z.string().max(60).default(''),
    })
        .default({}),
    whatsapp: z
        .object({
        driver: z.enum(['console', 'meta']).default('console'),
        phoneNumberId: z.string().max(80).default(''),
        accessToken: z.string().max(600).default(''),
    })
        .default({}),
    push: z
        .object({
        fcmProjectId: z.string().max(120).default(''),
        fcmClientEmail: z.string().max(200).default(''),
        fcmPrivateKey: z.string().max(6000).default(''),
    })
        .default({}),
    ai: z
        .object({
        provider: z.enum(['none', 'anthropic', 'openai']).default('none'),
        anthropicApiKey: z.string().max(300).default(''),
        anthropicModel: z.string().max(80).default('claude-sonnet-5'),
        openaiApiKey: z.string().max(300).default(''),
        openaiModel: z.string().max(80).default('gpt-4o-mini'),
    })
        .default({}),
});
export const DEFAULT_INTEGRATIONS = IntegrationsSettingsSchema.parse({});
// ── Catalogues ──────────────────────────────────────────────────────────────
/**
 * The option lists the admin panel offers in its forms.
 *
 * Two kinds live here, and the difference matters. `amenityCategories`,
 * `marketplaceCategories` and `starterBillLines` are free values — the
 * database stores whatever string is chosen, so the list can be anything.
 * `helperTypes`, `billingFrequencies` and `chargeBases` are labels over a
 * database enum: the *value* must be one the schema knows, and only the
 * wording is editable. A catalogue that let someone add a fourth charge basis
 * would produce a form that saves a value the API refuses.
 */
const OptionSchema = z.object({ value: z.string().min(1).max(40), label: z.string().min(1).max(80) });
const HintedOptionSchema = OptionSchema.extend({ hint: z.string().max(160).default('') });
/**
 * How a society runs its gate: how long a pass may be valid, how long a
 * pre-approval lasts by default for each kind of visitor, and how many
 * people one pass may bring. Read by the API when a pass is created and by
 * the app's pre-approve form, so the two can never disagree.
 */
/** One physical entrance. Guards pick one at the start of a shift. */
export const GateSchema = z.object({
    id: z.string().min(1).max(40),
    name: z.string().min(1).max(60),
    isDefault: z.boolean().default(false),
});
export const GateRulesSchema = z.object({
    /** The society's entrances. A society always has at least the main gate. */
    gates: z.array(GateSchema).min(1).max(20).default([{ id: 'main', name: 'Main gate', isDefault: true }]),
    /** A one-off pass may span at most this many days. */
    maxPassDays: z.number().int().min(1).max(60).default(7),
    /** A recurring (daily help) pass may run for at most this many days. */
    maxRecurringPassDays: z.number().int().min(7).max(365).default(180),
    /** How far back "arrives" may be set — a guest already at the gate. */
    passStartGraceHours: z.number().int().min(0).max(24).default(1),
    /** Default validity, in hours, by visitor type; OTHER is the fallback. */
    defaultPassHours: z
        .record(z.number().int().min(1).max(72))
        .default({ GUEST: 6, DELIVERY: 2, CAB: 2, SERVICE: 4, DOMESTIC_HELP: 4, VENDOR: 4, STAFF: 8, OTHER: 4 }),
    maxPartySize: z.number().int().min(1).max(200).default(50),
    /** A sign-on this many minutes after the rostered start still counts as on time. */
    rosterLateAfterMinutes: z.number().int().min(0).max(120).default(15),
    /** Whether gatekeepers may see their own duty hours (daily / weekly / monthly) in the gate app. */
    guardCanSeeDutyHours: z.boolean().default(false),
});
export const DEFAULT_GATE_RULES = GateRulesSchema.parse({});
/** The optional questions a pre-approve form can ask; name and time are always asked. */
export const VISITOR_FORM_FIELDS = ['phone', 'partySize', 'vehicle', 'company'];
/** Why someone is at the gate — the visitor register's enum. */
export const VISITOR_PURPOSE_VALUES = ['GUEST', 'DELIVERY', 'CAB', 'SERVICE', 'DOMESTIC_HELP', 'VENDOR', 'STAFF', 'OTHER'];
/** How a person relates to a flat. Fixed by the register's rules; the wording is the society's. */
export const RESIDENCY_VALUES = ['OWNER', 'TENANT', 'OWNER_NOT_RESIDING', 'FAMILY_MEMBER'];
/** The offices a resident can hold beside RESIDENT. Fixed by the permission model; the wording is the society's. */
export const COMMITTEE_ROLE_VALUES = ['TREASURER', 'SECRETARY', 'COMMITTEE_MEMBER', 'FACILITY_MANAGER'];
export const HELPER_TYPE_VALUES = [
    'MAID', 'COOK', 'DRIVER', 'NANNY', 'GARDENER', 'NEWSPAPER',
    'MILKMAN', 'LAUNDRY', 'PLUMBER', 'ELECTRICIAN', 'CARPENTER', 'OTHER',
];
export const BILLING_FREQUENCY_VALUES = ['MONTHLY', 'QUARTERLY', 'HALF_YEARLY', 'ANNUAL', 'ADHOC'];
export const CHARGE_BASIS_VALUES = ['FLAT', 'PER_SQFT', 'PER_BHK'];
export const CataloguesSettingsSchema = z.object({
    amenityCategories: z.array(OptionSchema).min(1).max(30).default([
        { value: 'CLUBHOUSE', label: 'Clubhouse' },
        { value: 'GYM', label: 'Gym' },
        { value: 'POOL', label: 'Pool' },
        { value: 'COURT', label: 'Court' },
        { value: 'HALL', label: 'Hall' },
        { value: 'GARDEN', label: 'Garden' },
        { value: 'PARKING', label: 'Parking' },
        { value: 'OTHER', label: 'Other' },
    ]),
    helperTypes: z
        .array(OptionSchema.extend({ value: z.enum(HELPER_TYPE_VALUES) }))
        .min(1)
        .max(HELPER_TYPE_VALUES.length)
        .default([
        { value: 'MAID', label: 'Maid' },
        { value: 'COOK', label: 'Cook' },
        { value: 'DRIVER', label: 'Driver' },
        { value: 'NANNY', label: 'Nanny' },
        { value: 'GARDENER', label: 'Gardener' },
        { value: 'NEWSPAPER', label: 'Newspaper' },
        { value: 'MILKMAN', label: 'Milk delivery' },
        { value: 'LAUNDRY', label: 'Laundry' },
        { value: 'PLUMBER', label: 'Plumber' },
        { value: 'ELECTRICIAN', label: 'Electrician' },
        { value: 'CARPENTER', label: 'Carpenter' },
        { value: 'OTHER', label: 'Other' },
    ]),
    residencyTypes: z
        .array(HintedOptionSchema.extend({ value: z.enum(RESIDENCY_VALUES) }))
        .min(1)
        .max(RESIDENCY_VALUES.length)
        .default([
        { value: 'OWNER', label: 'Owner, living here', hint: '' },
        { value: 'TENANT', label: 'Tenant', hint: '' },
        { value: 'OWNER_NOT_RESIDING', label: 'Owner, not living here', hint: '' },
        { value: 'FAMILY_MEMBER', label: 'Family member', hint: '' },
    ]),
    committeeRoles: z
        .array(HintedOptionSchema.extend({ value: z.enum(COMMITTEE_ROLE_VALUES) }))
        .min(1)
        .max(COMMITTEE_ROLE_VALUES.length)
        .default([
        { value: 'TREASURER', label: 'Treasurer', hint: 'Bills, payments and the ledger' },
        { value: 'SECRETARY', label: 'Secretary', hint: 'Members, notices and approvals' },
        { value: 'COMMITTEE_MEMBER', label: 'Committee member', hint: 'Sees everything, approves join requests' },
        { value: 'FACILITY_MANAGER', label: 'Facility manager', hint: 'Complaints, helpers and amenities' },
    ]),
    billingFrequencies: z
        .array(OptionSchema.extend({ value: z.enum(BILLING_FREQUENCY_VALUES) }))
        .min(1)
        .max(BILLING_FREQUENCY_VALUES.length)
        .default([
        { value: 'MONTHLY', label: 'Monthly' },
        { value: 'QUARTERLY', label: 'Quarterly' },
        { value: 'HALF_YEARLY', label: 'Half-yearly' },
        { value: 'ANNUAL', label: 'Annual' },
        { value: 'ADHOC', label: 'Ad-hoc — only when run by hand' },
    ]),
    chargeBases: z
        .array(HintedOptionSchema.extend({ value: z.enum(CHARGE_BASIS_VALUES) }))
        .min(1)
        .max(CHARGE_BASIS_VALUES.length)
        .default([
        { value: 'FLAT', label: 'The same for every flat', hint: 'A flat rate per home.' },
        { value: 'PER_SQFT', label: 'Per square foot', hint: 'The amount is multiplied by the carpet area.' },
        { value: 'PER_BHK', label: 'Per bedroom', hint: 'The amount is multiplied by the BHK count.' },
    ]),
    /** The lines a new billing profile starts with. */
    starterBillLines: z.array(z.string().min(1).max(80)).max(12).default([
        'Maintenance charges',
        'Water charges',
        'Sinking fund',
    ]),
    /**
     * Who can be pre-approved at the gate. The value is the register's enum
     * (fixed); the label and icon are what the app's chips and the guard's
     * screen show. Drop an entry and residents stop seeing that chip.
     */
    visitorTypes: z
        .array(OptionSchema.extend({
        value: z.enum(VISITOR_PURPOSE_VALUES),
        icon: z.string().max(40).default(''),
        /** Which extra questions the pre-approve form asks for this type. */
        fields: z.array(z.enum(VISITOR_FORM_FIELDS)).default(['phone', 'partySize', 'vehicle']),
    }))
        .min(1)
        .max(8)
        .default([
        { value: 'GUEST', label: 'Guest', icon: 'users', fields: ['phone', 'partySize', 'vehicle'] },
        { value: 'DELIVERY', label: 'Delivery', icon: 'package', fields: ['company', 'phone'] },
        { value: 'CAB', label: 'Cab', icon: 'car', fields: ['vehicle', 'company', 'phone'] },
        { value: 'SERVICE', label: 'Service', icon: 'wrench', fields: ['company', 'phone', 'partySize'] },
    ]),
    /** What a notice is about — the chip on the app's notice card and the admin's compose form. */
    noticeCategories: z.array(OptionSchema).min(1).max(20).default([
        { value: 'GENERAL', label: 'General' },
        { value: 'MAINTENANCE', label: 'Maintenance' },
        { value: 'EVENT', label: 'Event' },
        { value: 'FINANCE', label: 'Finance' },
        { value: 'SECURITY', label: 'Security' },
        { value: 'AGM', label: 'AGM' },
    ]),
    /** Kinds of society event, on the admin's event form and the app's event card. */
    eventCategories: z.array(OptionSchema).min(1).max(20).default([
        { value: 'FESTIVAL', label: 'Festival' },
        { value: 'MEETING', label: 'Meeting' },
        { value: 'SPORTS', label: 'Sports' },
        { value: 'CULTURAL', label: 'Cultural' },
        { value: 'WORKSHOP', label: 'Workshop' },
        { value: 'SOCIAL', label: 'Social' },
        { value: 'OTHER', label: 'Other' },
    ]),
    marketplaceCategories: z.array(z.string().min(1).max(40)).min(1).max(20).default([
        'Furniture',
        'Electronics',
        'Services',
        'Vehicles',
        'Free',
    ]),
    /** What a society admin can file a support ticket about. */
    supportCategories: z.array(OptionSchema).min(1).max(20).default([
        { value: 'BILLING', label: 'Billing & subscription' },
        { value: 'TECHNICAL', label: 'Something is not working' },
        { value: 'ACCOUNT', label: 'Account & access' },
        { value: 'FEATURE', label: 'Feature request' },
        { value: 'OTHER', label: 'Other' },
    ]),
});
export const DEFAULT_CATALOGUES = CataloguesSettingsSchema.parse({});
export const DEFAULT_BILLING = BillingSettingsSchema.parse({});
// ── Appearance ──────────────────────────────────────────────────────────────
/**
 * The fonts the consoles, the site and both apps may be set to.
 *
 * A fixed list rather than free text: every name here is a Google Font, which
 * is what lets the Flutter apps resolve it at runtime without a release, and
 * what lets the web load exactly one stylesheet instead of guessing.
 */
export const UI_FONTS = [
    'Inter',
    'Plus Jakarta Sans',
    'Manrope',
    'DM Sans',
    'Poppins',
    'Rubik',
    'Nunito Sans',
    'Work Sans',
    'Figtree',
    'Source Sans 3',
];
/** A 6-digit hex colour, with the hash. */
const hexColour = (fallback) => z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, 'Use a 6-digit hex colour, for example #4F46E5')
    .default(fallback);
/**
 * Colours and fonts for every surface — the two consoles, the marketing site,
 * and both mobile apps.
 *
 * The defaults are exactly the values that were previously compiled in, so a
 * platform that never opens this panel looks identical to before.
 */
export const AppearanceSettingsSchema = z.object({
    /** The brand colour: primary buttons, links, active navigation, focus rings. */
    primaryColor: hexColour('#4F46E5'),
    /** Used for secondary emphasis and chart series two. */
    accentColor: hexColour('#0D9488'),
    /** The platform console's dark navigation rail. */
    sidebarColor: hexColour('#111827'),
    /** The gate app's accent — deliberately its own, so a guard can tell the two apps apart. */
    gateAccentColor: hexColour('#F59E0B'),
    /** Body text everywhere. */
    fontBody: z.enum(UI_FONTS).default('Inter'),
    /** Headings and display type. */
    fontDisplay: z.enum(UI_FONTS).default('Plus Jakarta Sans'),
    /** Corner radius scale, in pixels, for cards and controls. */
    cornerRadius: z.number().int().min(0).max(24).default(10),
    /**
     * Whether the mobile apps follow these settings. Off keeps the apps on the
     * palette compiled into the build — useful while a theme is being tried out.
     */
    applyToApps: z.boolean().default(true),
});
export const DEFAULT_APPEARANCE = AppearanceSettingsSchema.parse({});
// ── Registry ────────────────────────────────────────────────────────────────
// ── Plan feature catalogue ──────────────────────────────────────────────────
export const PlanFeatureCatalogueSchema = z.object({
    groups: z
        .array(z.object({
        heading: z.string().min(1).max(60),
        features: z
            .array(z.object({
            /** What `requireFeature()` is called with. Lowercase, underscores. */
            key: z
                .string()
                .min(2)
                .max(40)
                .regex(/^[a-z][a-z0-9_]*$/, 'Lowercase letters, digits and underscores only.'),
            label: z.string().min(1).max(60),
            /**
             * A key the code gates on. It can be renamed and regrouped but not
             * deleted: removing it would leave `requireFeature()` asking about a
             * feature no plan can grant, which silently locks everyone out.
             */
            system: z.boolean().default(false),
        }))
            .max(80)
            .default([]),
    }))
        .max(12)
        .default([
        {
            heading: 'Core',
            features: [
                { key: 'announcements', label: 'Notices & polls', system: true },
                { key: 'bill_payments', label: 'Bill payments', system: true },
                { key: 'complaints', label: 'Complaints helpdesk', system: true },
                { key: 'visitor_management', label: 'Visitor management', system: true },
                { key: 'amenity_booking', label: 'Amenity booking', system: true },
                { key: 'documents', label: 'Document library', system: true },
                { key: 'sos', label: 'Emergency SOS', system: true },
                { key: 'accounting', label: 'Accounting', system: true },
            ],
        },
        {
            heading: 'Community',
            features: [
                { key: 'chat', label: 'Resident chat', system: true },
                { key: 'society_feed', label: 'Society feed', system: true },
                { key: 'marketplace', label: 'Marketplace', system: true },
                { key: 'paid_events', label: 'Paid event tickets', system: true },
                { key: 'daily_helpers', label: 'Daily helpers', system: true },
                { key: 'smart_cards', label: 'SMART cards', system: true },
                { key: 'move_in_out', label: 'Move-in / move-out', system: true },
            ],
        },
        {
            heading: 'Finance & operations',
            features: [
                { key: 'multi_block', label: 'Multiple blocks', system: true },
                { key: 'bank_reconciliation', label: 'Bank reconciliation', system: true },
                { key: 'bank_reconciliation_aa', label: 'Account Aggregator', system: true },
                { key: 'utility_tracker', label: 'Utility tracker', system: true },
                { key: 'asset_register', label: 'Asset register', system: true },
                { key: 'custom_reports', label: 'Custom reports', system: true },
            ],
        },
        {
            heading: 'Advanced',
            features: [
                { key: 'ai_triage', label: 'AI complaint triage', system: true },
                { key: 'iot_devices', label: 'IoT devices', system: true },
                { key: 'anpr', label: 'Number-plate recognition', system: true },
                { key: 'data_export_api', label: 'Data export API', system: true },
                { key: 'priority_support', label: 'Priority support', system: true },
                { key: 'white_label', label: 'White-labelled apps', system: true },
                { key: 'multi_society', label: 'Multi-society view', system: true },
            ],
        },
    ]),
});
export const DEFAULT_PLAN_FEATURES = PlanFeatureCatalogueSchema.parse({});
export const SETTING_SCHEMAS = {
    general: GeneralSettingsSchema,
    seo: SeoSettingsSchema,
    social_login: SocialLoginSettingsSchema,
    maintenance: MaintenanceSettingsSchema,
    mail: MailSettingsSchema,
    footer: FooterSettingsSchema,
    security: SecuritySettingsSchema,
    analytics: AnalyticsSettingsSchema,
    backup: BackupSettingsSchema,
    integrations: IntegrationsSettingsSchema,
    catalogues: CataloguesSettingsSchema,
    plan_features: PlanFeatureCatalogueSchema,
    billing: BillingSettingsSchema,
    appearance: AppearanceSettingsSchema,
};
export const SETTING_DEFAULTS = {
    general: DEFAULT_GENERAL,
    seo: DEFAULT_SEO,
    social_login: DEFAULT_SOCIAL_LOGIN,
    maintenance: DEFAULT_MAINTENANCE,
    mail: DEFAULT_MAIL,
    footer: DEFAULT_FOOTER,
    security: DEFAULT_SECURITY,
    analytics: DEFAULT_ANALYTICS,
    backup: DEFAULT_BACKUP,
    integrations: DEFAULT_INTEGRATIONS,
    catalogues: DEFAULT_CATALOGUES,
    plan_features: DEFAULT_PLAN_FEATURES,
    billing: DEFAULT_BILLING,
    appearance: DEFAULT_APPEARANCE,
};
/**
 * Fields never sent back to a browser.
 *
 * The API replaces each with a masked placeholder on read and only writes a
 * new value when one is supplied — so a settings page can be opened, edited
 * and saved without the secret ever leaving the server.
 */
export const WRITE_ONLY_SETTING_FIELDS = {
    // Apple's `privateKey` is a credential exactly like a client secret, and it
    // must never be echoed back to a browser either.
    social_login: ['clientSecret', 'privateKey'],
    mail: ['smtpPassword'],
    security: ['recaptchaSecretKey'],
    backup: ['googleDriveCredentials'],
    billing: ['secretKey', 'webhookSecret', 'clientSecret', 'keySecret', 'accessToken'],
    integrations: [
        'razorpayKeySecret',
        'razorpayWebhookSecret',
        'msg91AuthKey',
        'twilioAuthToken',
        'accessToken',
        'fcmPrivateKey',
        'anthropicApiKey',
        'openaiApiKey',
    ],
};
/** What a masked secret looks like on the wire. */
export const MASKED_SECRET = '••••••••';
//# sourceMappingURL=settings.js.map